const BACKEND_URL = process.env.REACT_APP_NEW_BASE_URL || 'https://staging-backend-v3-monitoring.de.energy';

export const fetchBillingRecords = async (params = {}) => {
  const queryParams = new URLSearchParams();
  
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      queryParams.append(key, value.toString());
    }
  });

  const url = `${BACKEND_URL}api/billing-records?${queryParams.toString()}`;

  try {
    const token = localStorage.getItem('accessToken');
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': token ? `Bearer ${token}` : '',
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching billing records:', error);
    throw error;
  }
};

export const createBillingRecord = async (data) => {
  const url = `${BACKEND_URL}api/billing-records`;

  try {
    const token = localStorage.getItem('accessToken');
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': token ? `Bearer ${token}` : '',
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || `HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error creating billing record:', error);
    throw error;
  }
};


export const updateBillingRecord = async (id, data) => {
  const url = `${BACKEND_URL}api/billing-records/${id}`;

  try {
    const token = localStorage.getItem('accessToken');
    const response = await fetch(url, {
      method: 'PATCH',
      headers: {
        'Authorization': token ? `Bearer ${token}` : '',
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || `HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error updating billing record:', error);
    throw error;
  }
};

export const deleteBillingRecord = async (id) => {
  const url = `${BACKEND_URL}api/billing-records/${id}`;

  try {
    const token = localStorage.getItem('accessToken');
    const response = await fetch(url, {
      method: 'DELETE',
      headers: {
        'Authorization': token ? `Bearer ${token}` : '',
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || `HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error deleting billing record:', error);
    throw error;
  }
};

export const fetchPlantBillingRecords = async (plantId, params = {}) => {
  return fetchBillingRecords({ ...params, plantId });
};

// Sum 'actGeneration' from Daily Generation Reports for the month of the given recordDate
export const fetchMonthlyActualGeneration = async (plantId, recordDate) => {
  try {
    if (!plantId || !recordDate) return 0;

    // Normalize recordDate to a Date
    const dateObj = typeof recordDate === 'string' || recordDate instanceof Date
      ? new Date(recordDate)
      : (recordDate && typeof recordDate.toDate === 'function')
        ? recordDate.toDate()
        : new Date(recordDate);

    const year = dateObj.getFullYear();
    const monthIndex = dateObj.getMonth(); // 0-based

    // Build YYYY-MM-DD strings for start and end of month
    const pad2 = (n) => String(n).padStart(2, '0');
    const startDate = `${year}-${pad2(monthIndex + 1)}-01`;
    // Last day of month: day 0 of next month
    const lastDay = new Date(year, monthIndex + 1, 0).getDate();
    const endDate = `${year}-${pad2(monthIndex + 1)}-${pad2(lastDay)}`;

    const token = localStorage.getItem('accessToken');
    const url = `${BACKEND_URL}api/daily-generation-reports?plantId=${encodeURIComponent(plantId)}&startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': token ? `Bearer ${token}` : '',
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      // Fail soft: log and return 0 so UI shows placeholder
      console.error('Failed to fetch DGR for monthly sum:', response.status, response.statusText);
      return 0;
    }

    const payload = await response.json();
    const rows = Array.isArray(payload?.data) ? payload.data : [];
    const sum = rows.reduce((acc, r) => acc + (Number(r?.actGeneration) || 0), 0);
    return Number(sum.toFixed(2));
  } catch (e) {
    console.error('Error fetching monthly actual generation from DGR:', e);
    return 0;
  }
};

//fetch billing record by plant and month
export const fetchBillingRecordByPlantAndMonth = async (plantId, recordDate) => {
  try {
    const token = localStorage.getItem('accessToken')
    
    if (!plantId || !recordDate) {
      return { 
        success: false, 
        data: null, 
        error: 'Plant ID and record date are required' 
      }
    }
    
    // Convert recordDate to YYYY-MM format for filtering
    const date = new Date(recordDate)
    const yearMonth = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
    
    const response = await fetch(
      `${BACKEND_URL}api/billing-records?plantId=${plantId}&recordDate=${yearMonth}`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
      }
    )

    if (!response.ok) {
      throw new Error(`Failed to fetch billing record: ${response.status}`)
    }

    const result = await response.json()
    
    // Return the first matching record (should only be one per plant per month)
    if (result.success && result.data && result.data.length > 0) {
      return { success: true, data: result.data[0] }
    }
    
    return { success: false, data: null }
  } catch (error) {
    console.error('Error fetching billing record:', error)
    return { success: false, data: null, error: error.message }
  }
}

//Update billing record with invoice details
export const updateBillingRecordWithInvoice = async (billingRecordId, invoiceData) => {
  try {
    const token = localStorage.getItem('accessToken')
    
    if (!billingRecordId) {
      throw new Error('Billing record ID is required')
    }
    
    // Map payment status from invoice format to billing record format
    const statusMapping = {
      'PAID': 'Paid',
      'UNPAID': 'Unpaid',
      'PENDING': 'Pending',
      'Paid': 'Paid',
      'Unpaid': 'Unpaid',
      'Pending': 'Pending',
      'To Be Raised': 'To Be Raised',
      'Draft': 'Draft'
    }
    
    // Format the data according to backend expectations
    const formattedData = {}
    
    if (invoiceData.invoiceNumber !== undefined) {
      formattedData.invoiceNumber = invoiceData.invoiceNumber
    }
    
    if (invoiceData.paymentStatus !== undefined) {
      // Map to the correct enum value
      formattedData.paymentStatus = statusMapping[invoiceData.paymentStatus] || invoiceData.paymentStatus
    }
    
    if (invoiceData.invoiceDate !== undefined) {
      // Convert YYYY-MM-DD to ISO 8601 date-time format
      const invoiceDate = new Date(invoiceData.invoiceDate)
      formattedData.invoiceDate = invoiceDate.toISOString()
    }
    
    if (invoiceData.dueDate !== undefined) {
      // Convert YYYY-MM-DD to ISO 8601 date-time format
      const dueDate = new Date(invoiceData.dueDate)
      formattedData.dueDate = dueDate.toISOString()
    }
    
    if (invoiceData.amount !== undefined) {
      formattedData.amount = parseFloat(invoiceData.amount)
    }
    
    if (invoiceData.currency !== undefined) {
      formattedData.currency = invoiceData.currency
    }
    
    console.log('Updating billing record:', billingRecordId, 'with data:', formattedData)
    
    const response = await fetch(
      `${BACKEND_URL}api/billing-records/${billingRecordId}`,
      {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(formattedData),
      }
    )

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      console.error('Billing record update failed:', errorData)
      throw new Error(errorData.error?.message || errorData.message || `Failed to update billing record: ${response.status}`)
    }

    const result = await response.json()
    console.log('Billing record updated successfully:', result)
    return { success: true, data: result }
  } catch (error) {
    console.error('Error updating billing record:', error)
    return { success: false, error: error.message }
  }
}


// Sync billing record from invoice changes
export const syncBillingRecordFromInvoice = async (plantId, invoiceDate, invoiceData) => {
  try {
    const token = localStorage.getItem('accessToken')
    const invoiceMonth = new Date(invoiceDate).toISOString().split('T')[0].substring(0, 7) // Get YYYY-MM format
    
    const url = `${BACKEND_URL}api/billing-records/sync-from-invoice`
    
    const syncData = {
      plantId: plantId,
      invoiceMonth: invoiceMonth,
      actualGeneration: invoiceData.energyValue,
      deemedGeneration: invoiceData.deemedEnergy,
      tariff: invoiceData.mainTariff,
      amount: invoiceData.invoiceValue,
      paymentStatus: invoiceData.paymentStatus,
      invoiceNumber: invoiceData.invoiceNumber,
      invoiceDate: invoiceData.invoiceDate,
      dueDate: invoiceData.dueDate,
    }
    
    console.log('Syncing billing record from invoice:', syncData)
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': token ? `Bearer ${token}` : '',
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(syncData),
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error?.message || `HTTP error! status: ${response.status}`)
    }

    const result = await response.json()
    console.log('Billing record synced from invoice:', result)
    return result
  } catch (error) {
    console.error('Error syncing billing record from invoice:', error)
    // Don't throw - log and continue (invoice update should succeed even if billing record sync fails)
    return { success: false, error: error.message }
  }
}

export const formatCurrency = (amount, currencyCode = 'INR') => {
  const formatters = {
    'INR': new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }),
    'USD': new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }),
    'EUR': new Intl.NumberFormat('en-EU', { style: 'currency', currency: 'EUR' }),
  };

  const formatter = formatters[currencyCode] || formatters['INR'];
  return formatter.format(amount);
};

export const calculateTotalAmount = (records) => {
  return records.reduce((total, record) => total + (record.amount || 0), 0);
};

export const getStatusColor = (status) => {
  const colors = {
    'PAID': 'success',
    'UNPAID': 'error',
    'PENDING': 'warning',
    'To Be Raised': 'default',
  };
  return colors[status] || 'default';
};

export const getStatusBadgeColor = (status) => {
  const colors = {
    'PAID': '#4caf50',
    'UNPAID': '#f44336',
    'PENDING': '#ff9800',
    'To Be Raised': '#9e9e9e',
  };
  return colors[status] || '#9e9e9e';
};

export const importBillingRecords = async (file, month) => {
  try {
    const token = localStorage.getItem('accessToken');
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('month', month);

    const url = `${BACKEND_URL}api/billing-records/import`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': token ? `Bearer ${token}` : '',
        'Accept': 'application/json',
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || `HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error importing billing records:', error);
    throw error;
  }
};
